import moment from "moment";

export function isNullOrZero(prop) {
  if (isNaN(prop)) return new Error("ERROR: isNullOrZero() can only take number params.");
  if (prop === null || prop === 0) return true;
  return false;
}

export function isNullOrEmpty(str) {
  if (str === null || str === "") return true;
  return false;
}

export function isDate(date) {
  return date instanceof Date;
}

export function getDateWithoutTime(date) {
  if (!(date instanceof Date)) return;
  return date.setHours(0, 0, 0, 0);
}

export function getLocalISOString(date) {
  var tzoffset = date.getTimezoneOffset() * 60000; //offset in milliseconds
  return new Date(date - tzoffset).toISOString().slice(0, -1);
}

export function formatEaTitle(jobStamp, title) {
  return jobStamp ? `${jobStamp.fullEA}: ${title}` : title;
}

export function toTitleCase(string) {
  const splitString = string.trim().toLowerCase().split(" ");
  for (let i = 0; i < splitString.length; i++) {
    splitString[i] = splitString[i][0].toUpperCase() + splitString[i].slice(1);
  }
  return splitString.join(" ");
}

export function convertMilTimeToMoment(date, milTime) {
  return moment().set({
    year: date.getFullYear(),
    month: date.getMonth(),
    date: date.getDate(),
    hours: parseInt(milTime.slice(0, 2), 10),
    minutes: parseInt(milTime.slice(2, 4), 10),
    second: 0,
  });
}

export function convertDateToMilTime(date) {
  const hrInt = date.getHours();
  const hr = hrInt < 10 ? "0" + hrInt.toString() : hrInt.toString();
  const minInt = date.getMinutes();
  const min = minInt < 10 ? "0" + minInt.toString() : minInt.toString();
  return hr + min;
}

/**
 * Generates a UUID
 * @returns {String} - UUID string
 */
export function getUUID() {
  var dt = new Date().getTime();
  var uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    var r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
}

export function tryParseJSON(stringData) {
  try {
    const obj = JSON.parse(stringData);
    return obj;
  } catch (e) {
    return {};
  }
}

/**
 * Conserve aspect ratio of the original region. Useful when shrinking/enlarging
 * images to fit into a certain area.
 *
 * @param {Number} srcWidth width of source image
 * @param {Number} srcHeight height of source image
 * @param {Number} maxWidth maximum available width
 * @param {Number} maxHeight maximum available height
 * @return {Object} { width, height }
 */
export function calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
  var ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return { width: srcWidth * ratio, height: srcHeight * ratio };
}

export function fileExtensionIsImage(ext) {
  let images = ["bmp", "gif", "png", "tiff", "tif", "jpg", "jpeg", "jpe"];
  if (images.includes(ext)) return true;
  return false;
}

export const cancellablePromise = (promise) => {
  let isCanceled = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      (value) => (isCanceled ? reject({ isCanceled, value }) : resolve(value)),
      (error) => reject({ isCanceled, error })
    );
  });

  return { promise: wrappedPromise, cancel: () => (isCanceled = true) };
};

export const delay = (t) => new Promise((resolve) => setTimeout(resolve, t));

/**
 * Returns a count of all leading zeros in a number string.
 * e.g. 000034 returns 4.
 * @param {string} number
 * @returns {number}
 */
export const getCountOfLeadingZeros = (numberStr) => {
  if (isNaN(+numberStr)) return 0;
  return (numberStr.toString().match(/^0+/) || [""])[0].length;
};

/**
 * Converts split station to a number representing station, e.g.
 * { fullSta: 1234, partialSta: 12.34 } into 123412.34.
 * The unary + conversion operator also removes all leading zeros.
 * 'toFixed' is needed to
 * @param {SplitStationString} splitSta
 * @returns {number}
 */
export const convertSplitStationToNumber = (splitSta) =>
  splitSta && !isNaN(+splitSta.fullSta) && !isNaN(+splitSta.partialSta)
    ? Math.abs(+splitSta.fullSta) * 100 +
      +(Math.round(Math.abs(+splitSta.partialSta) * 100) / 100).toFixed(2)
    : 0;

/**
 * Returns a combined station string (e.g. "1234+12.34 65' Lt").
 * For partial station string, leading zeros are added based on its length,
 * and rounded to 2 digit decimal.
 * Offset is optional only if the param is of type {SplitStationString}.
 * Offset value of 0 is discarded (by falsy splitSta.offset), since 0 offset
 * is not used (it's on the station line).
 * @param {SplitStationNumeric|SplitStationString} splitSta
 * @returns {string}
 */
export const convertSplitStationToString = (splitSta) => {
  if (splitSta && !isNaN(+splitSta.fullSta) && !isNaN(+splitSta.partialSta)) {
    // Pad with zeros for one-digit partialSta.
    const leadingZeros = "0".repeat(
      Math.abs(2 - Math.floor(+splitSta.partialSta).toString().length)
    );
    const partialSta =
      leadingZeros + (Math.round(Math.abs(+splitSta.partialSta) * 100) / 100).toString();
    // If offset is provided w/o offsetDir, ignore the offset.
    const offset = splitSta.offset
      ? !isNaN(+splitSta.offset) && splitSta.offsetDir
        ? splitSta.offset.toString() + "' " + splitSta.offsetDir
        : ""
      : "";

    return (
      Math.abs(+splitSta.fullSta).toString() +
      "+" +
      partialSta +
      (offset ? ", " + offset : "")
    );
  }
  return "(Number error)";
};

/**
 * Splits a number representing station to an object containing split
 * fullSta and partialSta - e.g. 123412.34 to { fullSta: 1234, partialSta: 12.34 }
 * @param {number} staInDecimal
 * @param {?number} offset - optional
 * @param {?string} offsetDir - optional
 * @returns {SplitStationNumeric}
 */
export const convertNumberToSplitStation = (
  staInDecimal,
  offset = null,
  offsetDir = ""
) => {
  if (isNaN(+staInDecimal)) return { fullSta: 0, partialSta: 0 };
  const fullSta = Math.floor(staInDecimal / 100);
  const partialSta = Math.round((staInDecimal % 100) * 100) / 100;
  return { fullSta, partialSta, offset, offsetDir };
};

export const getTimeOptions = () => {
  let timeOptions = [];
  let time = 0;
  let timeString = "";
  for (let t = 0; t < 96; t++) {
    if (t === 0) timeString = "0000";
    else {
      time = Math.floor(t * 0.25) * 100 + ((t * 15) % 60);
      timeString = time.toString();
      if (timeString.length === 2) timeString = "00" + timeString;
      if (timeString.length === 3) timeString = "0" + timeString;
    }
    timeOptions.push({ value: timeString, label: timeString });
  }
  return timeOptions;
};

/**
 * A try-catch wrapped JSON.stringify(). Return "" on error.
 * @param {Object} obj - input object to stringify
 * @returns {string}
 */
export const stringifyJSON = (obj) => {
  try {
    const string = JSON.stringify(obj);
    return string;
  } catch (e) {
    return "";
  }
};

/**
 * Returns first letters of each word in the input string.
 * @param {string} name - full name
 * @returns {string}
 */
export const getInitials = (name) => {
  if (!name) return "";
  const initArr = name.match(/\b(\w)/g);
  return initArr.join("").toUpperCase();
};

/**
 * Validates email format
 * @param {string} email - email string to validate
 * @returns {Boolean}
 */
export const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

export const appendFormData = (formDataToUpdate, files, inputObj) => {
  (files || []).forEach((file, i) => formDataToUpdate.append(i, file));
  Object.entries(inputObj).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v, i) => {
        // If non-array obj:
        if (v !== null && !Array.isArray(v) && typeof v === "object") {
          Object.entries(v).forEach(([oKey, oValue]) => {
            formDataToUpdate.append(`${key}[${i}].${oKey}`, oValue);
          });
        } else formDataToUpdate.append(`${key}[${i}]`, v);
      });
    } else formDataToUpdate.append(key, value);
  });

  return formDataToUpdate;
};
